import React, { useEffect } from 'react';
import { ExternalFlowStatus, ExternalConnectionError } from '../../../../interfaces/externalConnection.interface';
import useAppToast from '../../../../hooks/useAppToast';
import CommerciumLoader from '../../../../utils/CommerciumLoader';

export interface ExternalConnectionStatusUIProps {
  status: ExternalFlowStatus;
  error: ExternalConnectionError | null;
  loadingMessage?: string;
  successMessage?: string;
}

export const ExternalConnectionStatusUI: React.FC<ExternalConnectionStatusUIProps> = ({
  status,
  error,
  loadingMessage = 'Processing, please wait...',
  successMessage = 'Redirecting you now...'
}) => {
  const { errorToast } = useAppToast();

  useEffect(() => {
    if (status === 'error' && error) {
      errorToast(error.message || 'An unexpected error occurred during the bridge flow.');
    }
  }, [status, error, errorToast]);

  if (status === 'idle') return null;

  const goBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  if (status === 'error' && error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] p-8 text-center" role="status" aria-live="polite">
        <div className="mb-6 p-6 border border-red-200 rounded-xl bg-red-50 text-red-800 max-w-xl shadow-sm">
          <h2 className="text-2xl font-bold mb-2">Connection Error</h2>
          <p className="text-sm font-mono bg-red-100 py-1 px-2 rounded inline-block mb-3">
            Error Code: {error.code}
          </p>
          <p className="text-lg">{error.message}</p>
        </div>
        <button 
          onClick={goBack}
          className="px-8 py-3 rounded-lg bg-gray-800 hover:bg-gray-900 text-white font-semibold transition-all shadow-md active:scale-95"
        >
          Go Back
        </button>
      </div>
    );
  }

  const isBusy = status === 'loading' || status === 'redirecting' || status === 'success';
  const displayMessage = status === 'success' || status === 'redirecting' ? successMessage : loadingMessage;

  if (isBusy) {
    return (
      <div className="w-full" role="status" aria-live="polite" aria-busy="true">
        <CommerciumLoader message={displayMessage} />
      </div>
    );
  }

  return null;
};
